import { Coordinate, Nullable } from 'modules/types/common';

/* Provider Summary Type from ProviderSearch Service */
export interface ProviderSummary {
  callClinicForNPV: boolean;
  score: number;
  providerId: string;
  corpProvId: number;
  locationId: number;
  ncid: string;
  npi: number;
  cernerResourceId: number;
  cernerPersonnelId: number;
  firstName: string;
  middleName: string;
  lastName: string;
  title: string;
  displayName: string;
  gender: string;
  rating: number | null;
  specialties: Specialty[];
  primarySpecialty: Specialty;
  locations: ProviderLocation[];
  primaryLocation?: ProviderLocation;
  profileImage: string;
  isSelectHealth?: 'Y' | 'N' | null;
  isICentraPcp: boolean;
  messagingPools: MessagePool[];
  providerProfileImageUrl: string;
  providerProfileVideoId: string | null;
  primaryHospitalAffiliation: null | AffiliatedHospital;
  city: string;
  hasOnlineNpvSlots: boolean;
  isAcceptingOnlineBooking: boolean;
  isAcceptingMyInsurance: boolean;
  isPcp: boolean;
  isPreferred?: boolean;
  providerType: 'pcp' | 'preferred' | 'past';
  offersVideoVisits: boolean;
  distance: string;
}

export interface ProviderLocation {
  id: number;
  name: string;
  alternativeName: string | null;
  phoneNumber: string;
  address: {
    addressLine1: string;
    addressLine2: string | null;
    city: string;
    state: string;
    postalCode: string;
    country: string | null;
    isPrimary: boolean;
    latitude: number;
    longitude: number;
  };
  distance: string;
  inNetwork: boolean;
  hours: {
    is24Hours: boolean | null;
    sunday: string | null;
    monday: string | null;
    tuesday: string | null;
    wednesday: string | null;
    thursday: string | null;
    friday: string | null;
    saturday: string | null;
  };
  insurances: InsuranceSummary[];
  languages: Language[] | Languages[];
  profileImage: string;
}

export interface InsuranceSummary {
  id: string;
  name: string;
  hasOnlineNpvSlots: boolean;
  exceptions: string | null;
  aliasVal: string | null;
  primaryLocation: Nullable<Location>;
  primarySpecialty: Specialty;
  messagingPools: MessagePool[];
}

/* Insurance Type from getInsurance Service */
export interface Insurance {
  payerId: string;
  payerName: string;
  planId: string;
  planName: string;
}

export interface Address {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isPrimary: string;
  latitude: number;
  longitude: number;
}

export interface Location {
  locationId: string;
  ncid: string;
  name: string;
  address: Address;
  phoneNumber: string;
  locationImageUrl: string;
  distanceToUser: number;
  hasOnlineNpvSlots: boolean;
  rolledUpExceptions: RolledUpException;
  operatingHours: {
    additonalHoursInfo?: string;
    customHours?: string;
  };
}

export interface Specialty {
  specialtyId: string;
  specialtyDesc: string;
  specialtyName: string;
  shortSpecialtyName: string;
  isPrimary: boolean;
}

export interface OptionType {
  label: string;
  value: string;
}

export interface Disclaimer {
  isFetching: boolean;
  error: Record<string, any> | null;
  hasAcceptedTC?: boolean;
  hasDeclinedTC?: boolean;
  dateOfTC?: string;
  termsAndConditions?: string;
  tcVersion?: string;
}

export interface ProviderSearchFilters {
  filters: AzureFacetFilterObject[];
  facets: AzureFacetFilterObject[];
}

export interface ProviderSearchFilterState {
  hasActiveFilters: boolean;
  sortBy: OptionType;
  textName: string;
  locationText: string;
  geoLocation: Coordinate | null;
  activeFilters: ProviderSearchFilters;
}

export interface SearchResultNote {
  type: string;
  fieldName: string;
  message: string;
}

export type ProviderDetailCriteria = {
  id: string | number;
  idType: 'corpprovid' | 'ncid' | 'npi' | 'cernerid' | 'cernerPersonnelId';
} | null;

export interface ProviderDetail {
  callClinicForNPV: boolean;
  acceptingNewPatients: boolean;
  cernerPersonnelId: string;
  cernerResourceId: string;
  certifications: Certification[];
  chiefResidencies: Education[];
  corpProvId: string;
  degrees: Education[];
  displayName: string;
  fellowships: Fellowship[];
  firstName: string;
  gender: string;
  hospitalAffiliations: AffiliatedHospital[];
  isAcceptingMyInsurance: boolean;
  isAcceptingOnlineBooking: boolean;
  isHuntsman: boolean;
  isInterMountain: boolean;
  isMedicalGroupProvider: boolean;
  isMyHealthProvider: boolean;
  isSelectHealth: boolean | string;
  isSelectHealthIdaho: boolean;
  isStLukesProvider: boolean;
  languages: Languages[];
  lastName: string;
  locations: Location[];
  middleName: string;
  ncid: string;
  npi: string;
  primaryLocation?: Location;
  primarySpecialty?: Specialty;
  providerProfileImageUrl: string;
  providerProfileVideoId: string;
  providerProfileVideoUrl: string;
  ratings?: Rating;
  residencies: Education[];
  rolledUpExceptions: RolledUpException[];
  specialties: Specialty[];
  suffix: string;
  hasOnlineNpvSlots: boolean;
}

export interface Languages {
  languageCode: string;
  languageDescription: string;
}

export interface CommentsResult {
  totalCount: number;
  comments: Comment[];
}

export interface Comment {
  id: number;
  date: string;
  comment: string;
  overallRating: number;
}

export interface Rating {
  corpProvId: string;
  providerDisplayName: string;
  npi: string;
  overallRating?: number;
  totalCommentCount: number;
  totalRatingCount: number;
  totalSurveyCount: number;
  questionRatings: number;
  comments: Comment[];
}

export interface Education {
  corpProvId: number;
  levelId: number;
  levelDesc: string;
  programId: string;
  programDesc: string;
  institutionId: string;
  institutionDesc: string;
  institutionCity: string;
  institutionState: string;
  startDate: string;
  endDate: string;
  isGraduated: boolean;
  edSpecialtyId: string;
  edSpecialtyDesc: string;
}

interface Fellowship {
  educationId: string;
  levelId: number;
  levelDesc: string;
  programId: string;
  programDesc: string;
  degreeDesc: string;
  institutionId: string;
  institutionDesc: string;
  institutionCity: string;
  institutionState: string;
  startDate: string;
  endDate: string;
  isGraduated: boolean;
  edSpecialtyId: string;
  edSpecialtyDesc: string;
}

export interface Certification {
  corpProvId: number;
  boardId: number;
  boardDescription: string;
  awardDate: string;
  reverifyDate: string;
  expireDate: string;
  isLifetimeCertification: boolean;
}

export interface BaseClinics {
  clinicName: string;
  clinicId: string;
}

export interface BaseAffiliatedHospital {
  hospitalName: string;
  locationId: string;
}

export interface AffiliatedHospital extends BaseAffiliatedHospital {
  locationNcid: number;
  locationStatus: string;
  isPrimary: boolean;
  url: string;
}

export interface RolledUpException {
  locationName: string;
  insuranceName: string;
  exception: string;
}

export interface SuggestionDocument {
  '@search.text': string;
  corpProvId: string;
}

export type ProviderSpecialtiesDto = string;

export interface FacetDocument {
  count: number;
  value: string;
}

export interface ProviderFacets {
  'location/insurances/aliasVal': FacetDocument[];
  'ratings/overallRating': FacetDocument[];
  'locations/name': FacetDocument[];
  'location/insurances/ncid': FacetDocument[];
  'languages/languageDescription': FacetDocument[];
  gender: FacetDocument[];
  'location/locationId': FacetDocument[];
  yearsInPractice: FacetDocument[];
  'specialties/specialtyName': FacetDocument[];
}

export interface AzureFacetFilterObject {
  name?: string;
  group: string;
  value: string | number | boolean;
}

export interface ProviderSpecialties {
  group: string;
  name?: string;
  value: string;
}

export interface Language {
  code: string;
  description: string;
}

export interface MessagePool {
  alias: string;
  corpProvId: number;
  groupName: string;
  groupDisplayName: string;
  personnelGroupId: string;
  poolTypeName: 'POOL' | 'INBOX';
}

export interface ProviderSearchRequest {
  page: number;
  limit: number;
  orderBy?: string;
  fuzzy?: boolean;
  proximity?: number;
  input?: string;
  locationText?: string;
  userInsuranceAliasValues?: string[];
  returnFields?: string[];
  userInsurance?: string;
  myPCPList?: number[];
  myPreferredProvidersList?: number[];
  myPastProvidersList?: number[];
  location?: {
    lat: number | string;
    lng: number | string;
  };
  filters?: AzureFacetFilterObject[];
  facets?: AzureFacetFilterObject[];
}

export enum ProviderFilterGroups {
  offersOnlineBooking = 'offersOnlineBooking',
  hasAboutVideo = 'hasAboutVideo',
  hasPhoto = 'hasPhoto',
  acceptingNewPatients = 'acceptingNewPatients',
  isStLukes = 'isStLukes',
  isMyHealth = 'isMyHealth',
  isMedicalGroup = 'isMedicalGroup',
  acceptsChildren = 'acceptsChildren',
  isSelectHealth = 'isSelectHealth',
  isInterMountain = 'isInterMountain'
}

export enum ProviderFacetGroups {
  intermountainClinics = 'intermountainClinics',
  specialty = 'specialty',
  gender = 'gender',
  language = 'language',
  insurancePlanNcid = 'insurancePlanNcid',
  hospitalAffiliationList = 'hospitalAffiliationList',
  locationDistance = 'locationDistance',
  insuranceAliasValues = 'insuranceAliasValues'
}

export interface FilterItem {
  name: string;
  group: ProviderFacetGroups | ProviderFilterGroups;
  value: boolean | string | number;
}

export interface LocalFiltersObject {
  [key: string]: boolean;
}
export interface LocalFacetsObject {
  [key: string]: AzureFacetFilterObject[];
}
export interface AddComposeDocumentPayload {
  file: File;
  messageUUID: string;
}

export interface EducationDisplay {
  title: string;
  educations: Educations[];
}

export interface Educations {
  programId: string;
  institutionDesc: string;
  edSpecialtyDesc: string;
  startDate: string;
  endDate: string;
  institutionCity: string;
  institutionState: string;
  degreeDesc: string;
}
